import { ceil, floor, round } from 'lodash';

export function subtract(...nums: number[]): number {
  return subtractPrecision(2, ...nums);
}
export function subtractPrecision(precision: number = 2, ...nums: number[]): number {
  if (!nums.length) return 0;

  let difference = nums.shift() as number;
  for (const num of nums) {
    difference -= num;
  }
  return round(difference, precision);
}

export function add(...nums: number[]): number {
  return addPrecision(2, ...nums);
}
export function addPrecision(precision: number = 2, ...nums: number[]): number {
  let sum = 0;
  for (const num of nums) {
    sum += num;
  }
  return round(sum, precision);
}

export function multiply(...nums: number[]): number {
  return multiplyPrecision(2, ...nums);
}
export function multiplyPrecision(precision: number = 2, ...nums: number[]): number {
  const total = nums.reduce((acc, num) => acc * num, 1);
  return round(total, precision);
}

export function divide(numerator: number, denominator: number, roundingDigits: number = 2): number {
  return round(numerator / denominator, roundingDigits);
}

export function roundTo(
  method: 'up' | 'down' | 'nearest',
  numToRound: number,
  numToRoundTo: number,
  precision: number = 2,
): number {
  const percentage = numToRoundTo === 0 ? numToRound : numToRound / numToRoundTo;
  let roundedNum: number = 0;
  switch (method) {
    case 'up':
      const ceiling = ceil(percentage);
      roundedNum = numToRoundTo === 0 ? ceiling : ceiling * numToRoundTo;
      break;
    case 'down':
      const floored = floor(percentage);
      roundedNum = numToRoundTo === 0 ? floored : floored * numToRoundTo;
      break;
    case 'nearest':
      const nearest = round(percentage);
      roundedNum = numToRoundTo === 0 ? nearest : nearest * numToRoundTo;
      break;
    default:
      break;
  }

  return round(roundedNum, precision);
}

/**
 * This function is meant to solve a bug in remittance where a calculation of 100.055 rounds to 100.06
 * however a reversal of -100.055 rounds to -100.05 instead of -100.06
 *
 * This function should be used for calculations involving reversals and negative numbers
 */
export function absRound(num: number, precision: number = 2): number {
  const isNegative = num < 0;
  if (isNegative) {
    return round(Math.abs(num), precision) * -1;
  }
  return round(num, precision);
}

/**
 * Drain `pool` across `caps` in order, taking `min(remaining, max(cap, 0))`
 * from each slot. Returns the per-slot draw amounts (parallel to
 * `caps`) plus the leftover `remainder`. Currency-safe via
 * `add`/`subtract`.
 *
 * Use for any "spread a pool across N capped slots in priority order"
 * pattern. Callers assemble the result into whatever output shape
 * they need.
 */
export function waterfall(pool: number, caps: ReadonlyArray<number>): { drawn: number[]; remainder: number } {
  const drawn: number[] = [];
  let remaining = add(pool);
  for (const cap of caps) {
    if (remaining <= 0) {
      drawn.push(0);
      continue;
    }
    const draw = add(Math.min(remaining, Math.max(cap, 0)));
    drawn.push(draw);
    remaining = subtract(remaining, draw);
  }
  return { drawn, remainder: remaining };
}

/**
 * Keyed variant of {@link waterfall}. Pairs each cap with a key and
 * returns a sparse record of non-zero draws (omits zero entries).
 * Convenient when the per-slot results immediately reshape into a
 * named-field object.
 */
export function waterfallByKey<K extends string>(
  pool: number,
  slots: ReadonlyArray<readonly [K, number]>,
): { drawn: Partial<Record<K, number>>; remainder: number } {
  const { drawn: arr, remainder } = waterfall(
    pool,
    slots.map(([, c]) => c),
  );
  const drawn: Partial<Record<K, number>> = {};
  slots.forEach(([k], i) => {
    if (arr[i] > 0) drawn[k] = arr[i];
  });
  return { drawn, remainder };
}
